import { getFmxReferenceCache, saveFmxReferenceCache, getCacheAge, saveDependencyCache, deleteStaleDependencyCaches, getDependencyCache } from './db';
import { fmxFetch, fmxBatchFetch, claudeFetch, parseClaudeText } from './apiClient';
import { resolveEndpoint, resolvePostOptionsEndpoint, resolveGetOptionsEndpoint, resolvePutOptionsEndpoint } from './fmxEndpoints';
import { FMX_FIELD_TYPE_MAP } from './fmxFieldTypes';
import { FMX_FIELD_ENRICHMENTS, ENRICHMENT_VERSION } from './fmxFieldMetadata';
import { getBaseSchemaType } from './schemas';

// Credentials are encrypted at rest on the server (AES-256-GCM). The browser
// never handles the password after the initial save; it refers to stored creds
// by projectId. The old btoa/atob helpers were removed because Base64 is not
// encryption and anyone with DB read access could recover plaintext.

// Look up the saved email for display purposes only. Returns '' if no project
// carries an email column yet; components should fall back to an input field.
export function getSavedFmxEmail(project) {
  return project?.fmx_api_email || '';
}

export async function testFmxConnection(siteUrl, email, password) {
  try {
    const res = await fmxFetch({
      siteUrl: siteUrl.trim(), email: email.trim(), password,
      endpoint: '/v1/session/user', method: 'GET',
    });
    if (res.ok || res.status === 200) {
      let userName = '';
      try {
        const data = await res.json();
        userName = data.name || data.email || '';
      } catch {}
      const suffix = userName ? ` as ${userName}` : '';
      return { success: true, message: `Connected to ${siteUrl.trim()}${suffix}` };
    }
    return { success: false, message: `Connection failed (${res.status}) — check URL and credentials` };
  } catch {
    return { success: false, message: 'Connection failed — check URL and credentials' };
  }
}


// Convert any fmx_modules shape to the canonical new format:
//   { workRequestModules:[{slug,label}], scheduleRequestModules:[{slug,label}], workTaskModules:[{slug,label}] }
// Handles three cases:
//   1. Already fully-formed new format — pass-through
//   2. New format missing workTaskModules (saved before this fix) — backfill default
//   3. Old flat format { workRequest, scheduling, workTask } — convert to arrays
// Returns null for falsy input.
export function normalizeModules(raw) {
  if (!raw) return null;
  // Already fully-formed
  if (Array.isArray(raw.workRequestModules) && Array.isArray(raw.workTaskModules)) return raw;
  // New format missing workTaskModules (data saved before work-task independence fix)
  if (Array.isArray(raw.workRequestModules)) {
    return { ...raw, workTaskModules: [{ slug: 'maintenance', label: 'Maintenance' }] };
  }
  // Old flat format
  const cap = s => (s ? s.charAt(0).toUpperCase() + s.slice(1) : '');
  return {
    workRequestModules:    [{ slug: raw.workRequest || 'maintenance', label: cap(raw.workRequest || 'maintenance') }],
    scheduleRequestModules: [{ slug: raw.scheduling  || 'scheduling',  label: cap(raw.scheduling  || 'scheduling')  }],
    workTaskModules:       [{ slug: raw.workTask    || 'maintenance', label: cap(raw.workTask    || 'maintenance')  }],
  };
}

// Merges a freshly-fetched module list into the existing stored modules.
// Rules:
//   - Existing modules still in fresh → kept active (label updated if changed)
//   - Existing modules NOT in fresh   → marked { ..., disabled: true }
//   - Fresh modules not yet stored    → added as active
// Returns { merged, changed } where `changed` is true only if the result differs from existing.
export function mergeModules(existing, fresh) {
  const mergeList = (existingList = [], freshList = []) => {
    const freshMap  = new Map(freshList.map(m => [m.slug, m]));
    const existingSlugs = new Set(existingList.map(m => m.slug));
    const result = [];

    // Preserve existing order; update active/disabled status
    for (const m of existingList) {
      if (freshMap.has(m.slug)) {
        // Still present — ensure label is current, clear disabled flag
        result.push({ slug: m.slug, label: freshMap.get(m.slug).label });
      } else {
        // Gone from org → mark disabled (keep for history)
        result.push({ slug: m.slug, label: m.label, disabled: true });
      }
    }
    // Append brand-new modules from fresh
    for (const m of freshList) {
      if (!existingSlugs.has(m.slug)) {
        result.push({ slug: m.slug, label: m.label });
      }
    }
    return result;
  };

  const norm = existing || {};
  const merged = {
    workRequestModules:    mergeList(norm.workRequestModules,    fresh.workRequestModules),
    scheduleRequestModules: mergeList(norm.scheduleRequestModules, fresh.scheduleRequestModules),
    workTaskModules:       mergeList(norm.workTaskModules,       fresh.workTaskModules),
  };

  const changed = JSON.stringify(merged) !== JSON.stringify(norm);
  return { merged, changed };
}

// `creds` is an object with either { projectId } (preferred) or
// { siteUrl, email, password } (only for verify-before-save flows).
export async function fetchPostOptions(creds, schemaType, modules) {
  const endpoint = resolvePostOptionsEndpoint(schemaType, modules);
  if (!endpoint) return { customFields: [], systemFields: [] };

  try {
    const res = await fmxFetch({ ...creds, endpoint, method: 'GET' });
    if (!res.ok) return { customFields: [], systemFields: [] };
    const data = await res.json();

    const customFields = (data.customFields || [])
      .filter(cf => cf.key && cf.label)
      .map(cf => ({
        id: cf.key,
        name: cf.label,
        // Per CLAUDE.md §8f, the canonical field is `fieldType`. Any earlier
        // `fieldTypeName` fallback was undocumented.
        fieldType: cf.fieldType || 'Text',
        isRequired: cf.isRequired || false,
        options: cf.options || [],
        allowMultipleSelections: cf.allowMultipleSelections || false,
        allowOtherOption: cf.allowOtherOption || false,
        description: cf.description || '',
        defaults: cf.defaults || [],
      }));

    const systemFields = (data.systemFields || []).map(sf => ({
      key: sf.key,
      label: sf.label,
      isRequired: sf.isRequired || false,
      isPermitted: sf.isPermitted !== false,
      maximumLength: sf.maximumLength || null,
      minimumLength: sf.minimumLength || null,
      minimumValue: sf.minimumValue ?? null,
      maximumValue: sf.maximumValue ?? null,
      defaultValue: sf.defaultValue ?? null,
      defaultValues: sf.defaultValues || null,
      options: sf.options || null,
    }));

    return { customFields, systemFields };
  } catch (e) {
    console.warn('[FMX post-options] fetch failed:', e);
    return { customFields: [], systemFields: [] };
  }
}

// Map GET OPTIONS response keys (camelCase) → dep cache keys (kebab-case).
// Only keys with a consumer (DEP_KEY_TO_CROSS_SHEET validation or push-time
// resolveDepKey) belong here — anything else just bloats fmx_reference_cache.
const GET_OPTS_DEP_PROPS = {
  buildings:       'buildings',
  requestTypes:    'request-types',
  resources:       'resources',
  equipment:       'equipment',
  resourceTypes:   'resource-types',
  assignmentUsers: 'users',
};

async function fetchGetOptions(creds, schemaType, modules) {
  const endpoint = resolveGetOptionsEndpoint(schemaType, modules);
  if (!endpoint) return { customFields: [], raw: null, depMaps: {} };

  try {
    const res = await fmxFetch({ ...creds, endpoint, method: 'GET' });
    if (!res.ok) return { customFields: [], raw: null, depMaps: {} };
    const data = await res.json();

    // get-options returns customFields as map<id, name>, not an array
    const cfMap = (data.customFields && typeof data.customFields === 'object' && !Array.isArray(data.customFields))
      ? data.customFields : {};
    const customFields = Object.entries(cfMap).map(([id, name]) => ({
      id: parseInt(id, 10),
      name: String(name),
      fieldType: 'Text',
      isRequired: false,
    }));

    // Extract dep maps (buildings, requestTypes, resources, etc.)
    const depMaps = {};
    for (const [prop, cacheKey] of Object.entries(GET_OPTS_DEP_PROPS)) {
      const raw = data[prop];
      if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
        depMaps[cacheKey] = Object.entries(raw)
          .map(([id, name]) => ({ id: parseInt(id, 10), name: String(name) }))
          .filter(item => item.id > 0);
      }
    }

    return { customFields, raw: data, depMaps };
  } catch (e) {
    console.warn('[FMX get-options] fetch failed:', e);
    return { customFields: [], raw: null, depMaps: {} };
  }
}

// Auto-fetch module arrays from the FMX organization endpoint.
// Returns { workRequestModules, scheduleRequestModules, workTaskModules } each as [{slug,label}].
// Falls back to single-entry defaults if the org endpoint fails or returns no data.
//
// Actual response fields (confirmed from API):
//   data.workRequestSettings    — array,  each has .moduleKey (slug) and .moduleName (label)
//   data.scheduleRequestSettings — object, has .moduleKey and .moduleName
//   data.workTaskSettings       — array,  each has .moduleKey and .moduleName
// `creds` is an object with either { projectId } (preferred; for saved projects)
// or { siteUrl, email, password } (for the verify-before-save flow).
export async function fetchFmxModules(creds) {
  // Defaults used only until fetchFmxModules() confirms module slugs from /v1/organization.
  // 'maintenance' is an assumption, not a spec-backed value — CLAUDE.md §4 names it as a
  // common convention but does not prescribe a Work-Task default.
  const defaults = {
    orgName: null,
    workRequestModules:    [{ slug: 'maintenance', label: 'Maintenance' }],
    scheduleRequestModules: [{ slug: 'scheduling',  label: 'Scheduling'  }],
    workTaskModules:       [{ slug: 'maintenance', label: 'Maintenance' }],
  };
  try {
    const res = await fmxFetch({ ...creds, endpoint: '/v1/organization', method: 'GET' });
    if (!res.ok) return defaults;
    const data = await res.json();

    const orgName = data.name || null;
    const modules = {
      workRequestModules: defaults.workRequestModules,
      scheduleRequestModules: defaults.scheduleRequestModules,
      workTaskModules: defaults.workTaskModules,
    };

    // Work request modules — data.workRequestSettings is an array
    const wrSettings = data.workRequestSettings || [];
    if (Array.isArray(wrSettings) && wrSettings.length > 0) {
      modules.workRequestModules = wrSettings.map(m => ({
        slug:  m.moduleKey,
        label: m.moduleName,
      }));
    }

    // Schedule request — data.scheduleRequestSettings is a single object (not array)
    const srSettings = data.scheduleRequestSettings;
    if (srSettings?.moduleKey) {
      modules.scheduleRequestModules = [{ slug: srSettings.moduleKey, label: srSettings.moduleName }];
    }

    // Work task modules — data.workTaskSettings is an array, independent of work requests
    const wtSettings = data.workTaskSettings || [];
    if (Array.isArray(wtSettings) && wtSettings.length > 0) {
      modules.workTaskModules = wtSettings.map(m => ({
        slug:  m.moduleKey,
        label: m.moduleName,
      }));
    }

    return { orgName, ...modules };
  } catch (e) {
    console.warn('[FMX modules] fetch failed, using defaults:', e);
    return defaults;
  }
}

// Infer custom field types using rule-based heuristics and Claude AI as a fallback.
// Fields that already have a recognized type, have options (dropdowns), or are checkbox-like
// are resolved without calling Claude. The remainder are batched into one Claude request —
// unless `useAi` is false (callers that don't need precise types, e.g. the Attachment
// Field Scan tool, skip the Claude call entirely and keep the rule-based defaults).
async function inferCustomFieldTypes(customFields, useAi = true) {
  const validTypes = new Set(Object.keys(FMX_FIELD_TYPE_MAP));

  const result = customFields.map(cf => {
    // Already a recognized type — keep it
    if (validTypes.has(cf.fieldType)) return { ...cf };
    // Has selectable options or multi-select → DropDownList
    if (cf.options?.length > 0 || cf.allowMultipleSelections) return { ...cf, fieldType: 'DropDownList' };
    // Default to Text; AI will override below
    return { ...cf, fieldType: 'Text' };
  });

  if (!useAi) return result;

  // Collect fields that fell through to Text and aren't obviously text
  const needsAI = result.filter((cf, i) => cf.fieldType === 'Text' && !customFields[i].options?.length);
  if (needsAI.length === 0) return result;

  try {
    const fieldList = needsAI.map(cf => `"${cf.name}"`).join(', ');
    const typeList = Object.entries(FMX_FIELD_TYPE_MAP)
      .map(([k, v]) => `${k}: ${v.label}`)
      .join(', ');

    const aiData = await claudeFetch({
      max_tokens: 512,
      system: 'You are a data field classifier. Respond ONLY with a valid JSON object, no markdown, no explanation.',
      messages: [{
        role: 'user',
        content: `Given these FMX custom field names: ${fieldList}\n\nClassify each into one of these types: ${typeList}\n\nDefault to "Text" if unsure. Respond with JSON: { "Field Name": "TypeKey", ... }`,
      }],
    });

    const text = parseClaudeText(aiData);
    const suggestions = JSON.parse(text);
    for (const cf of result) {
      if (cf.fieldType === 'Text' && suggestions[cf.name] && validTypes.has(suggestions[cf.name])) {
        cf.fieldType = suggestions[cf.name];
      }
    }
  } catch (e) {
    console.warn('[inferCustomFieldTypes] AI inference failed, defaulting to Text:', e);
  }

  return result;
}

// Main sync entry point — takes full project object and schemaType string.
// Options:
//   inferTypes — when false, skip the Claude field-type inference pass and
//     keep rule-based defaults. For callers (Attachment Field Scan) where
//     types are only loose hints and no AI call should fire on page entry.
//   writeCache — when false, don't persist the catalog to the shared 24h
//     reference cache. Paired with inferTypes:false so a Text-typed catalog
//     never poisons the cache the import wizard reads. Dependency-map writes
//     still happen (they aren't type-inferred and other surfaces need them).
export async function syncFmxDataForProject(project, schemaType, forceRefresh = false, { inferTypes = true, writeCache = true } = {}) {
  if (!project?.fmx_credentials || !project?.fmx_site_url) {
    return { customFields: [], systemFields: [], fromCache: false };
  }

  const projectId = project.id;
  const modules = project.fmx_modules || {};

  // Check cache first (24h TTL). Caches written under an older enrichment
  // registry are treated as stale — their synthetic systemFields carry
  // retired field keys that would produce wrong payloads.
  if (projectId && !forceRefresh) {
    const age = await getCacheAge(projectId, schemaType);
    if (age < 24) {
      const cached = await getFmxReferenceCache(projectId, schemaType);
      if (cached?.extra?.enrichmentVersion === ENRICHMENT_VERSION
          && cached?.extra?.customFields?.length > 0) {
        const customFields = cached.extra.customFields.map(cf => ({
          id: cf.id,
          name: cf.name,
          fieldType: cf.fieldType,
          isRequired: cf.isRequired || false,
          options: cf.options || [],
          allowMultipleSelections: cf.allowMultipleSelections || false,
          allowOtherOption: cf.allowOtherOption || false,
          description: cf.description || '',
          defaults: cf.defaults || [],
        }));
        const systemFields = cached.extra.systemFields || [];
        return { customFields, systemFields, fromCache: true };
      }
    }
  }

  const creds = { projectId };

  try {
    // Fetch both /post-options (system field metadata) and /get-options (custom fields + dep values) in parallel
    const [postOpts, getOpts] = await Promise.all([
      fetchPostOptions(creds, schemaType, modules),
      fetchGetOptions(creds, schemaType, modules),
    ]);

    // Build synthetic fields from GET OPTIONS sortKeys + enrichments.
    // Fields that appear in sortKeys AND have an enrichment entry become mappable
    // system fields even if they're absent from post-options.
    const sortKeys = getOpts.raw?.sortKeys || {};
    const baseType = getBaseSchemaType(schemaType);
    const enrichments = FMX_FIELD_ENRICHMENTS[baseType] || {};
    const existingSystemFieldKeys = new Set(postOpts.systemFields.map(sf => sf.key));

    // 1) SortKey-driven synthetic fields: keys in sortKeys that have a matching enrichment.
    //    `isRequired` falls back to the enrichment because these fields have no
    //    post-options record to carry an API-reported value.
    const syntheticFields = Object.keys(sortKeys)
      .filter(k => enrichments[k] && !existingSystemFieldKeys.has(k))
      .map(k => ({
        key: k,
        label: enrichments[k].label || sortKeys[k],
        isRequired: enrichments[k].isRequired === true,
        isPermitted: true,
      }));

    // 2) Enrichment-driven synthetic fields: ALL enrichment entries not already covered.
    //    This ensures non-lookup fields like dueDate and date/time fields always appear.
    const syntheticKeys = new Set(syntheticFields.map(s => s.key));
    const enrichmentSyntheticFields = Object.entries(enrichments)
      .filter(([key, enrich]) =>
        enrich.label &&
        !existingSystemFieldKeys.has(key) &&
        !syntheticKeys.has(key)
      )
      .map(([key, enrich]) => ({
        key,
        label: enrich.label,
        isRequired: enrich.isRequired === true,
        isPermitted: true,
      }));

    const systemFields = [...postOpts.systemFields, ...syntheticFields, ...enrichmentSyntheticFields];

    // Merge custom fields: /post-options is the authoritative source (has full metadata: id via cf.key,
    // label, fieldType, options, allowMultipleSelections, etc.). /get-options only has a name map and
    // supplements if it contains fields that post-options missed.
    const postFields = postOpts.customFields;
    const mergedCustomFields = [
      ...postFields,
      ...getOpts.customFields.filter(cf => !postFields.find(p => p.id === cf.id)),
    ];

    // Infer/assign field types via rules + AI (AI pass skippable)
    const customFields = await inferCustomFieldTypes(mergedCustomFields, inferTypes);

    // Save dep maps from GET OPTIONS to dep cache
    if (projectId && getOpts.depMaps) {
      for (const [depKey, items] of Object.entries(getOpts.depMaps)) {
        if (items.length > 0) {
          await saveDependencyCache(projectId, depKey, items, items.length);
        }
      }
    }

    if (projectId && writeCache) {
      await saveFmxReferenceCache(projectId, schemaType, customFields, systemFields, ENRICHMENT_VERSION);
    }

    return { customFields, systemFields, fromCache: false };
  } catch (e) {
    console.error('syncFmxDataForProject error:', e);
    return { customFields: [], systemFields: [], fromCache: false };
  }
}

// --- Dependency update system ---

// DEPENDENCY_TYPES drives the top-level "Update Dependencies" sync and the
// DependenciesView UI. Some deps are module-scoped — see moduleScope:
//   • 'bucket-by-module' (request-types): one global fetch with ?fields=…,module;
//     results are split into per-module caches keyed `{key}:{slug}` plus a
//     consolidated `{key}` cache for UI / backward compat.
//   • 'path-per-module:workTask' (instruction-sets): fetched once per Work Task
//     module slug via `/v1/{slug}/instruction-sets`, stored as `{key}:{slug}`
//     plus a consolidated `{key}` cache tagged with `module` per item.
// Consumers (fmxTransform.resolveDepKey) pick the module-specific bucket based
// on the current import's schema slug; they fall back to the consolidated
// cache when no per-module bucket exists.
export const DEPENDENCY_TYPES = [
  { key: 'buildings',       endpoint: '/v1/buildings',        label: 'Buildings',             nameField: 'name' },
  { key: 'resources',       endpoint: '/v1/resources',        label: 'Resources & Locations', nameField: 'name' },
  { key: 'users',           endpoint: '/v1/users',            label: 'Users',                 nameField: 'name', extraFields: ['email'] },
  { key: 'equipment-types', endpoint: '/v1/equipment-types',  label: 'Equipment Types',       nameField: 'name', extraFields: ['isAssetLifespanTracked'] },
  { key: 'equipment',       endpoint: '/v1/equipment',        label: 'Equipment Names',       nameField: 'tag' },
  { key: 'inventory-types', endpoint: '/v1/inventory-types',  label: 'Inventory Types',       nameField: 'name' },
  { key: 'inventory',       endpoint: '/v1/inventory',        label: 'Inventory Names',       nameField: 'name' },
  { key: 'request-types',   endpoint: '/v1/request-types',    label: 'Request Types',         nameField: 'name', moduleScope: 'bucket-by-module', extraFields: ['module'] },
  { key: 'user-types',      endpoint: '/v1/user-types',       label: 'User Types',            nameField: 'name' },
  { key: 'resource-types', endpoint: '/v1/resource-types',   label: 'Resource Types',        nameField: 'name' },
  { key: 'work-task-instruction-sets',
    endpoint: (slug) => `/v1/${slug}/instruction-sets`,
    label: 'Work Task Instruction Sets',
    nameField: 'name',
    moduleScope: 'path-per-module:workTask' },
];

// How many page sub-requests to pack into a single batch-proxy call. 10 pages ×
// limit 100 = up to 1,000 records per response body — comfortably under serverless
// response limits while collapsing 10 credential lookups into one.
const PAGES_PER_BATCH = 10;

function parsePage(data) {
  return Array.isArray(data) ? data : (data?.items || data?.data || data?.results || []);
}

// Generic paginated fetcher — collects all pages from an FMX list endpoint.
//
// Strategy: fetch page 1 via the single-shot proxy (doubles as auth/endpoint
// sanity check and yields FMX-Total-Count), then fan the remaining pages out
// through the batch proxy with bounded server-side concurrency. Falls back to a
// sequential loop when the total-count header is absent, only one page exists,
// or no projectId is available to batch with (verify-before-save creds).
//
// NOTE: `limit` stays at 100. The FMX spec documents no maximum, and the
// "items.length < limit ⇒ last page" termination below is only safe while the
// server honors the requested limit — a silent server-side clamp on a larger
// limit would drop records without error. Speed comes from fan-out, not bigger pages.
export async function fetchAllPages(creds, endpoint, fields = 'id,name', limit = 100) {
  const sep = endpoint.includes('?') ? '&' : '?';
  const fieldsParam = `&fields=${encodeURIComponent(fields)}`;
  const pageUrl = (off) => `${endpoint}${sep}offset=${off}&limit=${limit}${fieldsParam}`;

  const first = await fmxFetch({ ...creds, endpoint: pageUrl(0), method: 'GET' });
  if (!first.ok) throw new Error(`FMX returned ${first.status} for ${endpoint}`);
  const headerTotal = first.headers.get('FMX-Total-Count');
  const totalCount = headerTotal !== null ? parseInt(headerTotal, 10) : null;
  const firstItems = parsePage(await first.json());

  // Single page (under a full page = last page), or we can't safely fan out.
  if (firstItems.length < limit) {
    return { items: firstItems, totalCount: totalCount ?? firstItems.length };
  }
  const canFanOut = creds?.projectId && totalCount !== null && totalCount > limit;
  if (!canFanOut) {
    return fetchAllPagesSequential(creds, endpoint, fields, limit, firstItems, totalCount);
  }

  // Fan out remaining offsets through the batch proxy.
  const offsets = [];
  for (let off = limit; off < totalCount; off += limit) offsets.push(off);

  const byOffset = new Map([[0, firstItems]]);
  for (let i = 0; i < offsets.length; i += PAGES_PER_BATCH) {
    const slice = offsets.slice(i, i + PAGES_PER_BATCH);
    const results = await fmxBatchFetch({
      projectId: creds.projectId,
      items: slice.map(off => ({ key: off, endpoint: pageUrl(off), method: 'GET' })),
    });
    for (const r of results) {
      if (r.ok) {
        byOffset.set(r.key, parsePage(r.body));
      } else {
        // One client-side retry of just this page, then fail loudly (matches old throw).
        const retry = await fmxFetch({ ...creds, endpoint: pageUrl(r.key), method: 'GET' });
        if (!retry.ok) throw new Error(`FMX returned ${retry.status} for ${endpoint} (offset ${r.key})`);
        byOffset.set(r.key, parsePage(await retry.json()));
      }
    }
  }

  // Tail guard: the table can grow past page-1's totalCount while we fan out, so
  // the frozen offset list would miss the new rows. If the highest page came back
  // full, keep paging sequentially until a short page (matches the old loop).
  let tail = offsets.length ? offsets[offsets.length - 1] : 0;
  while ((byOffset.get(tail)?.length || 0) === limit) {
    tail += limit;
    const res = await fmxFetch({ ...creds, endpoint: pageUrl(tail), method: 'GET' });
    if (!res.ok) throw new Error(`FMX returned ${res.status} for ${endpoint} (offset ${tail})`);
    byOffset.set(tail, parsePage(await res.json()));
  }

  // Assemble in offset order; dedupe by id so a mid-fetch data shift (totalCount
  // moving, rows sliding across page boundaries) can't double-count records.
  const seen = new Set();
  const allItems = [];
  for (const off of [...byOffset.keys()].sort((a, b) => a - b)) {
    for (const it of byOffset.get(off)) {
      const id = it?.id;
      if (id != null) {
        if (seen.has(id)) continue;
        seen.add(id);
      }
      allItems.push(it);
    }
  }
  return { items: allItems, totalCount: allItems.length };
}

// Sequential continuation from page 2 onward. Used when fan-out isn't safe.
async function fetchAllPagesSequential(creds, endpoint, fields, limit, firstItems, totalCount) {
  const sep = endpoint.includes('?') ? '&' : '?';
  const allItems = [...firstItems];
  let offset = limit;
  while (true) {
    if (totalCount !== null && allItems.length >= totalCount) break;
    const url = `${endpoint}${sep}offset=${offset}&limit=${limit}&fields=${encodeURIComponent(fields)}`;
    const res = await fmxFetch({ ...creds, endpoint: url, method: 'GET' });
    if (!res.ok) throw new Error(`FMX returned ${res.status} for ${endpoint}`);
    const items = parsePage(await res.json());
    allItems.push(...items);
    if (items.length < limit) break;
    offset += limit;
  }
  return { items: allItems, totalCount: totalCount ?? allItems.length };
}

// Which dep keys each schema base type needs for validation and push-time
// name→ID resolution. This is the ONLY copy — keep it in sync with the
// crossSheet/lookup entries in FMX_FIELD_ENRICHMENTS (fmxFieldMetadata.js).
// null = not in map = fetch all (safe default for unknown types).
// The single source of truth for which dependency types each schema needs
// (the old divergent copy in fmxFieldMetadata.js was deleted). Keep entries in
// sync with the lookup/crossSheet enrichments when a schema gains a new lookup.
const SCHEMA_DEP_KEYS = {
  'Building':               [],
  'Resource':               ['buildings', 'resource-types', 'user-types'],
  'User':                   ['buildings', 'user-types', 'equipment'],
  'Equipment Type':         [],
  'Equipment':              ['buildings', 'equipment-types', 'resources', 'equipment', 'inventory', 'users'],
  'Inventory':              ['buildings', 'inventory-types', 'resources'],
  'Work Request':           ['buildings', 'users', 'resources', 'request-types', 'equipment'],
  'Schedule Request':       ['buildings', 'resources', 'request-types'],
  'Work Task':              ['buildings', 'users', 'equipment', 'resources', 'request-types', 'work-task-instruction-sets'],
  'Transportation Request': ['buildings', 'resources', 'request-types', 'users'],
  'Accounting Account':     [],
  'Requisition':            ['buildings', 'users', 'request-types'],
  'Utility Provider':       [],
  'Equipment Log':          ['equipment'],
  'Inventory Adjustment':   ['inventory'],
  'Inventory Transfer':     ['inventory'],
};

/** Returns the dep keys needed for a given schema type, or null if unknown (fetch all). */
export function getDepKeysForSchema(schemaType) {
  if (!schemaType) return null;
  const base = schemaType.indexOf(':') === -1 ? schemaType : schemaType.slice(0, schemaType.indexOf(':'));
  const keys = SCHEMA_DEP_KEYS[base];
  return Array.isArray(keys) ? keys : null; // null = unknown type → fetch all
}

// Fetch dependencies for a project in parallel.
// depKeys: optional string[] to limit which types are fetched (schema-aware auto-sync).
// Calls onTypeProgress(depKey, 'done'|'error', count) as each type completes.
export async function fetchAllDependencies(project, onTypeProgress, depKeys = null) {
  if (!project?.fmx_credentials || !project?.fmx_site_url) {
    throw new Error('Missing FMX credentials');
  }

  const projectId = project.id;
  const creds = { projectId };

  // Filter to only the requested types; if depKeys is empty array, nothing to fetch
  const depsToFetch = depKeys === null
    ? DEPENDENCY_TYPES
    : DEPENDENCY_TYPES.filter(d => depKeys.includes(d.key));

  if (depsToFetch.length === 0) return {};

  const modules = normalizeModules(project.fmx_modules) || {};

  // Collect the active module slugs a given moduleScope should expand across.
  // Disabled modules (mergeModules flags historical ones) are skipped — their
  // data isn't needed for new imports but may still live in older caches.
  const slugsFor = (scope) => {
    const kind = scope.startsWith('path-per-module:') ? scope.split(':')[1] : null;
    let list = [];
    if (kind === 'workTask')          list = modules.workTaskModules || [];
    else if (kind === 'workRequest')  list = modules.workRequestModules || [];
    else if (kind === 'scheduleRequest') list = modules.scheduleRequestModules || [];
    return list.filter(m => !m.disabled).map(m => m.slug);
  };

  const settled = await Promise.allSettled(
    depsToFetch.map(async dep => {
      try {
        // Module-scoped: one fetch per module slug at a templated path.
        // Stores per-slug caches + a tagged aggregate (`{key}`) so the UI can
        // render a unified view while resolvers pick a specific bucket.
        if (typeof dep.moduleScope === 'string' && dep.moduleScope.startsWith('path-per-module:')) {
          const slugs = slugsFor(dep.moduleScope);
          if (slugs.length === 0) {
            if (onTypeProgress) onTypeProgress(dep.key, 'done', 0);
            return { key: dep.key, count: 0, status: 'done' };
          }
          const fields = ['id', dep.nameField, ...(dep.extraFields || [])].join(',');
          const endpointFn = typeof dep.endpoint === 'function' ? dep.endpoint : (s) => dep.endpoint;
          let aggregate = [];
          let totalCount = 0;
          for (const slug of slugs) {
            const path = endpointFn(slug);
            let items = [];
            let count = 0;
            try {
              const res = await fetchAllPages(creds, path, fields);
              items = res.items;
              count = res.totalCount;
            } catch (inner) {
              // Per-module failure: skip this slug but keep others. Surface as
              // zero-count rather than aborting the whole dep.
              console.warn(`[${dep.key}] slug "${slug}" fetch failed:`, inner);
              continue;
            }
            const cleaned = items.map(item => {
              const entry = { id: item.id, name: item[dep.nameField], module: slug };
              // Keep the raw field too (e.g. equipment `tag`) so consumers
              // aren't forced through the `name` alias.
              if (dep.nameField !== 'name') entry[dep.nameField] = item[dep.nameField];
              for (const ef of (dep.extraFields || [])) entry[ef] = item[ef];
              return entry;
            });
            await saveDependencyCache(projectId, `${dep.key}:${slug}`, cleaned, count);
            aggregate = aggregate.concat(cleaned);
            totalCount += count;
          }
          await saveDependencyCache(projectId, dep.key, aggregate, totalCount);
          // Drop buckets for module slugs that no longer exist. Active slugs
          // are all kept — including ones whose fetch failed this round.
          await deleteStaleDependencyCaches(projectId, dep.key, slugs.map(s => `${dep.key}:${s}`));
          if (onTypeProgress) onTypeProgress(dep.key, 'done', totalCount);
          return { key: dep.key, count: totalCount, status: 'done' };
        }

        // Bucket-by-module: single global fetch, split client-side by each
        // item's `module` field into per-slug caches + a consolidated cache.
        // Items whose module is empty/null land ONLY in the aggregate — they
        // aren't assignable to a specific module bucket.
        const fields = ['id', dep.nameField, ...(dep.extraFields || [])].join(',');
        const { items, totalCount } = await fetchAllPages(creds, typeof dep.endpoint === 'function' ? dep.endpoint() : dep.endpoint, fields);

        const cleaned = items.map(item => {
          const entry = { id: item.id, name: item[dep.nameField] };
          // Keep the raw field too (e.g. equipment `tag`) so consumers
          // aren't forced through the `name` alias.
          if (dep.nameField !== 'name') entry[dep.nameField] = item[dep.nameField];
          for (const ef of (dep.extraFields || [])) entry[ef] = item[ef];
          return entry;
        });

        if (dep.moduleScope === 'bucket-by-module') {
          const buckets = {};
          for (const entry of cleaned) {
            const slug = entry.module;
            if (!slug) continue;
            (buckets[slug] ||= []).push(entry);
          }
          for (const [slug, bucketItems] of Object.entries(buckets)) {
            await saveDependencyCache(projectId, `${dep.key}:${slug}`, bucketItems, bucketItems.length);
          }
          // Drop buckets for module slugs absent from this full refetch.
          await deleteStaleDependencyCaches(projectId, dep.key, Object.keys(buckets).map(slug => `${dep.key}:${slug}`));
        }

        await saveDependencyCache(projectId, dep.key, cleaned, totalCount);
        if (onTypeProgress) onTypeProgress(dep.key, 'done', totalCount);
        return { key: dep.key, count: totalCount, status: 'done' };
      } catch (e) {
        console.warn(`Failed to fetch dependency "${dep.key}":`, e);
        if (onTypeProgress) onTypeProgress(dep.key, 'error', 0);
        return { key: dep.key, count: 0, status: 'error', error: e.message };
      }
    })
  );

  const results = {};
  for (const r of settled) {
    if (r.status === 'fulfilled') results[r.value.key] = r.value;
  }
  return results;
}

// --- Scheduling rate types ---
// FMX has no dedicated scheduling-rate-types endpoint. Each user type's
// scheduleRequestSettings.schedulingRateType names the rate type that user
// type is billed at, so the distinct set across user types is the site's
// rate-type list. Used to pre-populate the Resource wizard's scheduling-rate
// buckets. Live fetch first (rate types change rarely but the list is tiny);
// falls back to the dependency cache when the fetch fails (e.g. offline resume).
export async function syncSchedulingRateTypes(project) {
  const projectId = project?.id;
  if (!projectId) return [];
  try {
    const { items } = await fetchAllPages({ projectId }, '/v1/user-types', 'id,name,scheduleRequestSettings');
    const seen = new Set();
    const names = [];
    for (const item of items) {
      const rate = item?.scheduleRequestSettings?.schedulingRateType;
      const trimmed = typeof rate === 'string' ? rate.trim() : '';
      if (trimmed && !seen.has(trimmed)) { seen.add(trimmed); names.push(trimmed); }
    }
    if (names.length > 0) {
      await saveDependencyCache(projectId, 'scheduling-rate-types', names.map((name, i) => ({ id: i + 1, name })), names.length);
    }
    return names;
  } catch (e) {
    console.warn('[scheduling-rate-types] live fetch failed, falling back to cache:', e);
    const cached = await getDependencyCache(projectId, 'scheduling-rate-types');
    return (cached?.extra?.items || []).map(i => i.name).filter(Boolean);
  }
}
